import type { IPurchaseRepository } from '../repository/interfaces/purchase.repository.interface';

export interface ResourcePurchase {
  id: string;
  userId: string;
  resourceId: string;
  instructorId?: string | null;
  sellerId?: string | null;
  status: string;
  totalAmount: number;
  title: string;
  itemType: string;
  purchasedAt: unknown;
  createdAt: unknown;
  updatedAt: unknown;
}

export interface SalesSummary {
  totalRevenue: number;
  totalSales: number;
  purchases: ResourcePurchase[];
}

export interface ResourceSalesBreakdown {
  resourceId: string;
  title: string;
  salesCount: number;
  totalRevenue: number;
  buyers: Array<{
    userId: string;
    purchasedAt: any;
    totalAmount: number;
  }>;
}

export class SalesService {
  constructor(private purchaseRepo: IPurchaseRepository) {}

  /**
   * Get all sales for an instructor/seller
   * Queries both instructorId and sellerId for backward compatibility
   */
  async getInstructorSales(ownerId: string): Promise<SalesSummary> {
    console.log('[SalesService.getInstructorSales] START - OwnerId:', ownerId);

    try {
      const rows = await this.purchaseRepo.getResourcePurchasesForOwner(ownerId);
      const purchases = rows.map(
        (row) =>
          ({
            id: row.id,
            ...row,
          }) as ResourcePurchase,
      );

      const totalRevenue = purchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
      const totalSales = purchases.length;

      console.log('[SalesService.getInstructorSales] SUCCESS - Found', totalSales, 'sales, $', totalRevenue);

      return {
        totalRevenue,
        totalSales,
        purchases,
      };
    } catch (error) {
      console.error('[SalesService.getInstructorSales] ERROR:', error);
      throw new Error('Failed to fetch sales data');
    }
  }

  /**
   * Get sales breakdown by resource
   * Groups sales by resourceId and provides detailed stats
   */
  async getSalesBreakdown(ownerId: string): Promise<ResourceSalesBreakdown[]> {
    console.log('[SalesService.getSalesBreakdown] START - OwnerId:', ownerId);

    try {
      const { purchases } = await this.getInstructorSales(ownerId);

      const resourceMap = new Map<string, ResourceSalesBreakdown>();

      purchases.forEach((purchase) => {
        const resourceId = purchase.resourceId;

        if (!resourceMap.has(resourceId)) {
          resourceMap.set(resourceId, {
            resourceId,
            title: purchase.title,
            salesCount: 0,
            totalRevenue: 0,
            buyers: [],
          });
        }

        const resource = resourceMap.get(resourceId)!;
        resource.salesCount += 1;
        resource.totalRevenue += purchase.totalAmount || 0;
        resource.buyers.push({
          userId: purchase.userId,
          purchasedAt: purchase.purchasedAt,
          totalAmount: purchase.totalAmount,
        });
      });

      const breakdown = Array.from(resourceMap.values());

      breakdown.sort((a, b) => b.totalRevenue - a.totalRevenue);

      console.log('[SalesService.getSalesBreakdown] SUCCESS - ', breakdown.length, 'unique resources');

      return breakdown;
    } catch (error) {
      console.error('[SalesService.getSalesBreakdown] ERROR:', error);
      throw new Error('Failed to fetch sales breakdown');
    }
  }

  /**
   * Get recent sales with limit
   */
  async getRecentSales(ownerId: string, limitCount: number = 10): Promise<ResourcePurchase[]> {
    console.log('[SalesService.getRecentSales] START - OwnerId:', ownerId);

    try {
      const { purchases } = await this.getInstructorSales(ownerId);

      return purchases.slice(0, limitCount);
    } catch (error) {
      console.error('[SalesService.getRecentSales] ERROR:', error);
      throw new Error('Failed to fetch recent sales');
    }
  }
}
